// Per-stop result viewers (spec §2 "Output view" column).
import { useEffect, useMemo, useRef, useState } from 'react'

import {
  AudioSection,
  formatTimecode,
  type TranscriptSegment,
} from '@/components/grounding/audio-section'
import { JsonTree } from '@/components/json-tree'
import { MarkdownPane } from '@/components/markdown-pane'
import { PdfPane, type PdfSource } from '@/components/pdf-pane'
import { outputUrl } from '@/lib/api'
import { sampleUrl, type Stop } from '@/lib/demos'
import { blockId, type ParseJson, parseOverlays, wordCount } from '@/lib/parse-overlays'
import { cn } from '@/lib/utils'

import type { ProvenanceEntry } from '@/components/grounding/provenance-types'
import type { Input, RunState } from '@/hooks/use-demo-run'

type Run = NonNullable<RunState['result']>['run']
type Citation = {
  fileId?: string
  page?: number
  bbox?: ProvenanceEntry['bbox']
  timecode?: number | string
  text?: string
  notFound?: boolean
}

const Split = ({
  left,
  right,
  className,
  mobilePane,
  mobileControls,
}: {
  left: React.ReactNode
  right: React.ReactNode
  className?: string
  mobilePane?: 'left' | 'right'
  mobileControls?: React.ReactNode
}) => (
  <div className="flex h-full min-h-0 flex-col">
    {mobileControls}
    <div
      className={cn(
        'grid min-h-0 flex-1 grid-cols-1 divide-y divide-border lg:grid-cols-2 lg:divide-x lg:divide-y-0',
        className,
      )}
    >
      <div
        className={cn(
          'min-h-[40vh] min-w-0 overflow-hidden lg:min-h-0',
          mobilePane === 'right' && 'hidden lg:block',
        )}
      >
        {left}
      </div>
      <div
        className={cn(
          'min-h-[40vh] min-w-0 overflow-hidden lg:min-h-0',
          mobilePane === 'left' && 'hidden lg:block',
        )}
      >
        {right}
      </div>
    </div>
  </div>
)
const Pane = ({
  title,
  children,
  className,
}: {
  title: string
  children: React.ReactNode
  className?: string
}) => (
  <div className={cn('flex h-full min-h-0 flex-col', className)}>
    <div className="flex h-9 flex-none items-center border-b border-border px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {title}
    </div>
    <div className="min-h-0 flex-1 overflow-auto">{children}</div>
  </div>
)

export function OutputView({
  stop,
  state,
  input,
  origin,
}: {
  stop: Stop
  state: RunState
  input: Input
  origin: string
}) {
  const run = state.result?.run
  const output = useInlineOutput(run)
  const source: PdfSource | undefined =
    input.source === 'sample'
      ? { url: sampleUrl(origin, input.file) }
      : input.file.size
        ? { file: input.file }
        : undefined
  const ownUrl = useObjectUrl(input.source === 'own' ? input.file : undefined)
  const audioUrl = input.source === 'sample' ? sampleUrl(origin, input.file) : ownUrl
  const name = input.file instanceof File ? input.file.name : input.file

  switch (stop.id) {
    case 'parse-md':
      return (
        <Split
          left={<Pane title="Source">{<PdfPane source={source} name={name} />}</Pane>}
          right={
            <Pane title="Markdown">
              <MarkdownPane markdown={String(output.markdown ?? '')} />
            </Pane>
          }
        />
      )
    case 'parse-blocks':
      return <BlocksView source={source} name={name} json={output.json as ParseJson | undefined} />
    case 'parse-ocr':
      return (
        <OcrCompare
          output={output}
          source={source}
          name={name}
          processing={input.source === 'own' ? (input.upload?.processing ?? 'auto') : undefined}
        />
      )
    case 'extract-invoice':
      return (
        <ExtractView
          key={run?.id}
          source={source}
          name={name}
          value={output.value}
          citations={output.citations as Record<string, Citation[]> | undefined}
        />
      )
    case 'transcribe':
      return (
        <div className="flex h-full min-h-0 p-3">
          <AudioSection url={audioUrl} segments={segmentsOf(output.json)} />
        </div>
      )
    case 'call-notes':
      return (
        <NotesView
          audioUrl={audioUrl}
          segments={segmentsOf(transcriptOf(run))}
          value={output.value}
          citations={output.citations as Record<string, Citation[]> | undefined}
        />
      )
    case 'redact':
      return <RedactView run={run} source={source} name={name} />
    case 'compose-invoice':
      return (
        <Pane title="Generated PDF">
          <PdfPane
            source={state.bytes ? { bytes: state.bytes } : outFile(run)}
            name="invoice.pdf"
          />
        </Pane>
      )
    case 'fill-form':
      return <FillView run={run} />
  }
}

/** Inline markdown/json, or the proxied output when the gateway left them out (>1 MiB). */
function useInlineOutput(run: Run | undefined) {
  const output = (run?.output ?? {}) as Record<string, unknown>
  const [fetched, setFetched] = useState<Record<string, unknown>>({})
  useEffect(() => {
    let active = true
    setFetched({})
    if (!run || !String(run.object ?? '').startsWith('parse')) return () => undefined
    for (const name of ['markdown', 'json'] as const) {
      if (output[name] !== undefined) continue
      void fetch(outputUrl(run.id, name)).then(async (r) => {
        if (!r.ok) return
        const v = name === 'json' ? await r.json() : await r.text()
        if (active) setFetched((f) => ({ ...f, [name]: v }))
      })
    }
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.id])
  return { ...fetched, ...output }
}

function useObjectUrl(file: File | undefined) {
  const [url, setUrl] = useState('')
  useEffect(() => {
    if (!file?.size) return setUrl('')
    const next = URL.createObjectURL(file)
    setUrl(next)
    return () => URL.revokeObjectURL(next)
  }, [file])
  return url
}

const segmentsOf = (json: unknown): TranscriptSegment[] => {
  const s = (json as { segments?: unknown } | undefined)?.segments
  return Array.isArray(s) ? (s as TranscriptSegment[]) : []
}
/** An extract run on audio carries the parsed source inline (`?output=inline`). */
const transcriptOf = (run: Run | undefined) => {
  const o = (run?.output ?? {}) as { json?: unknown; documents?: { json?: unknown }[] }
  return o.json ?? o.documents?.[0]?.json
}
const outFile = (run: Run | undefined): PdfSource | undefined => {
  const f =
    ((run?.output ?? {}) as { file?: { name: string }; files?: { name: string }[] }).file ??
    (run?.output as { files?: { name: string }[] })?.files?.[0]
  return run && f ? { url: outputUrl(run.id, f.name) } : undefined
}

function BlocksView({
  source,
  name,
  json,
}: {
  source?: PdfSource
  name: string
  json?: ParseJson
}) {
  const [active, setActive] = useState<string>()
  const overlays = useMemo(() => parseOverlays(json, active), [json, active])
  // Tree rows are `p<page>:<i>` too, so hover on either side lights the same box.
  const tree = useMemo(
    () =>
      Object.fromEntries(
        (json?.pages ?? []).map((p, pi) => [
          `page ${pi + 1}`,
          Object.fromEntries(
            (p.textItems ?? []).map((t, i) => [
              blockId(pi, i),
              `${t.text ?? ''}  [${Math.round(t.x ?? 0)}, ${Math.round(t.y ?? 0)}, ${Math.round(t.width ?? 0)}×${Math.round(t.height ?? 0)}]`,
            ]),
          ),
        ]),
      ),
    [json],
  )
  return (
    <Split
      left={
        <Pane title={`${overlays.length} blocks`}>
          <PdfPane
            source={source}
            name={name}
            overlays={overlays}
            onOverlayHover={setActive}
            onOverlayClick={setActive}
          />
        </Pane>
      }
      right={
        <Pane title="JSON · x, y, w×h in points">
          <div className="p-2">
            <JsonTree
              value={tree}
              active={active}
              onHover={setActive}
              onLeaf={setActive}
              leafId={(p) => p.split('.').pop()!}
            />
          </div>
        </Pane>
      }
    />
  )
}

function OcrCompare({
  output,
  source,
  name,
  processing,
}: {
  output: Record<string, unknown>
  source?: PdfSource
  name: string
  /** Set for an own upload: one pass with this processing, no simple-vs-ocr claim. */
  processing?: string
}) {
  const ocr = output as { markdown?: string; json?: ParseJson }
  // ponytail: the sample runs OCR only; a scan has no text layer, so the simple
  // pass is 0 words by construction. Read `run.simple` if a proxy ever adds it.
  return (
    <div className="grid h-full min-h-0 grid-rows-[auto_1fr]">
      <div className="grid grid-cols-2 divide-x divide-border border-b border-border text-center">
        {processing ? null : (
          <div className="p-3">
            <div className="font-mono text-xs text-muted-foreground">processing: "simple"</div>
            <div className="font-heading text-2xl">0 words</div>
            <div className="text-[11px] text-muted-foreground">no text layer</div>
          </div>
        )}
        <div className={cn('p-3', processing && 'col-span-2')}>
          <div className="font-mono text-xs text-muted-foreground">
            processing: "{processing ?? 'ocr'}"
          </div>
          <div className="font-heading text-2xl text-primary">{wordCount(ocr.json)} words</div>
          <div className="text-[11px] text-muted-foreground">
            {processing === 'auto'
              ? 'OCR only where a page has no text layer'
              : 'page_ocr 20 credits vs page_parsed 5'}
          </div>
        </div>
      </div>
      <Split
        left={
          <Pane title={processing ? 'Source' : 'Scan'}>
            <PdfPane source={source} name={name} />
          </Pane>
        }
        right={
          <Pane title="Markdown">
            <MarkdownPane markdown={ocr.markdown ?? ''} />
          </Pane>
        }
      />
    </div>
  )
}

const firstCitation = (citations: Record<string, Citation[]> | undefined, path: string) => {
  // Citation keys are top-level field keys; nested paths fall back to their root.
  const c = citations?.[path]?.[0] ?? citations?.[path.split('.')[0]!]?.[0]
  return c && !c.notFound ? c : undefined
}

function ExtractView({
  source,
  name,
  value,
  citations,
}: {
  source?: PdfSource
  name: string
  value: unknown
  citations?: Record<string, Citation[]>
}) {
  const [active, setActive] = useState<string>()
  const [mobilePane, setMobilePane] = useState<'output' | 'source'>('output')
  const sourceToggleRef = useRef<HTMLButtonElement>(null)
  const cite = active ? firstCitation(citations, active) : undefined
  const citation: ProvenanceEntry | undefined = cite?.bbox
    ? { fileId: cite.fileId, page: cite.page, bbox: cite.bbox, text: cite.text }
    : undefined
  const outputPane = (
    <Pane title="Extracted · click a value to see where it came from">
      <div className="p-2">
        <JsonTree
          value={value ?? {}}
          onLeaf={(path) => {
            setActive(path)
            setMobilePane('source')
            requestAnimationFrame(() => sourceToggleRef.current?.focus())
          }}
          active={active}
        />
      </div>
    </Pane>
  )
  const sourcePane = (
    <Pane title="Source">
      <PdfPane source={source} name={name} citation={citation} />
    </Pane>
  )
  return (
    <Split
      mobilePane={mobilePane === 'output' ? 'left' : 'right'}
      mobileControls={
        <div
          role="group"
          aria-label="Extraction result view"
          className="grid grid-cols-2 border-b border-border p-1 lg:hidden"
        >
          {(['output', 'source'] as const).map((pane) => (
            <button
              key={pane}
              ref={pane === 'source' ? sourceToggleRef : undefined}
              type="button"
              aria-pressed={mobilePane === pane}
              onClick={() => setMobilePane(pane)}
              className="rounded-sm px-3 py-1.5 text-xs font-medium capitalize text-muted-foreground aria-pressed:bg-muted aria-pressed:text-foreground"
            >
              {pane}
            </button>
          ))}
        </div>
      }
      left={outputPane}
      right={sourcePane}
    />
  )
}

function NotesView({
  audioUrl,
  segments,
  value,
  citations,
}: {
  audioUrl: string
  segments: TranscriptSegment[]
  value: unknown
  citations?: Record<string, Citation[]>
}) {
  const [seek, setSeek] = useState<{ to: number; n: number }>()
  const [active, setActive] = useState<string>()
  const onLeaf = (path: string) => {
    setActive(path)
    const c = firstCitation(citations, path)
    const t = typeof c?.timecode === 'string' ? Number(c.timecode) : c?.timecode
    if (typeof t === 'number' && Number.isFinite(t)) setSeek((s) => ({ to: t, n: (s?.n ?? 0) + 1 }))
  }
  return (
    <Split
      left={
        <Pane title="Clinical note · click a field to hear it">
          <ul className="flex flex-col gap-2 p-3">
            {Object.entries((value ?? {}) as Record<string, unknown>).map(([k, v]) => {
              const c = firstCitation(citations, k)
              const t = typeof c?.timecode === 'string' ? Number(c.timecode) : c?.timecode
              return (
                <li key={k}>
                  <button
                    type="button"
                    onClick={() => onLeaf(k)}
                    className={cn(
                      'w-full rounded-md border border-border p-2 text-left text-sm hover:bg-muted focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none',
                      active === k && 'border-primary bg-primary/5',
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-xs text-muted-foreground">{k}</span>
                      {typeof t === 'number' ? (
                        <span className="rounded-full bg-primary/10 px-1.5 font-mono text-[10px] text-primary">
                          {formatTimecode(t)}
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-0.5">
                      {Array.isArray(v)
                        ? v.length
                          ? v.join(', ')
                          : '—'
                        : v == null || v === ''
                          ? '—'
                          : String(v)}
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
        </Pane>
      }
      right={
        <div className="flex h-full min-h-0 p-3">
          <AudioSection
            url={audioUrl}
            segments={segments}
            seekTo={seek?.to}
            seekRequest={seek?.n}
          />
        </div>
      }
    />
  )
}

function RedactView({ run, source, name }: { run?: Run; source?: PdfSource; name: string }) {
  const entities = ((run?.output as { entities?: Record<string, number> } | undefined)?.entities ??
    {}) as Record<string, number>
  const total = Object.values(entities).reduce((a, b) => a + b, 0)
  return (
    <div className="grid h-full min-h-0 grid-rows-[auto_1fr]">
      <div className="flex flex-wrap items-center gap-1.5 border-b border-border p-2 text-xs">
        <span className="mr-1 font-medium">{total} redactions</span>
        {Object.entries(entities).map(([k, n]) => (
          <span
            key={k}
            className="rounded-full border border-border px-2 py-0.5 text-muted-foreground"
          >
            {k} <span className="font-mono text-foreground">{n}</span>
          </span>
        ))}
      </div>
      <Split
        left={
          <Pane title="Before">
            <PdfPane source={source} name={name} />
          </Pane>
        }
        right={
          <Pane title="After">
            <PdfPane source={outFile(run)} name="redacted.pdf" />
          </Pane>
        }
      />
    </div>
  )
}

function FillView({ run }: { run?: Run }) {
  const fields = ((run?.output as { fields?: Record<string, unknown> } | undefined)?.fields ??
    {}) as Record<string, unknown>
  return (
    <Split
      className="lg:grid-cols-[2fr_3fr]"
      left={
        <Pane title={`${Object.keys(fields).length} values written to the template`}>
          <ul className="flex flex-col divide-y divide-border">
            {Object.entries(fields).map(([k, v]) => (
              <li key={k} className="grid grid-cols-[1fr_1fr] gap-2 px-3 py-1.5 text-xs">
                <span className="font-mono text-muted-foreground">{k}</span>
                <span>{v == null || v === '' ? '—' : String(v)}</span>
              </li>
            ))}
          </ul>
        </Pane>
      }
      right={
        <Pane title="Filled certificate">
          <PdfPane source={outFile(run)} name="filled.pdf" />
        </Pane>
      }
    />
  )
}
